import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LucideIcon } from "lucide-react";
import { motion } from "framer-motion";

interface ModernKPICardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  trend?: {
    value: number;
    label: string;
  };
  iconColor?: string;
  index?: number;
}

export function ModernKPICard({ 
  title, 
  value, 
  icon: Icon, 
  trend, 
  iconColor = "text-primary",
  index = 0
}: ModernKPICardProps) {
  const isPositiveTrend = trend && trend.value > 0;
  const isNegativeTrend = trend && trend.value < 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ y: -4, scale: 1.02 }}
    >
      <Card className="border-border/50 hover:border-primary/30 transition-all hover:shadow-xl bg-gradient-to-br from-card to-card/50">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4 sm:p-6">
          <CardTitle className="text-xs sm:text-sm font-medium leading-tight">{title}</CardTitle>
          <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0`}>
            <Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${iconColor}`} />
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <div className="text-2xl sm:text-3xl font-bold tracking-tight bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
            {value}
          </div>
          {trend && (
            <div className="flex items-center gap-1.5 sm:gap-2 mt-2">
              <Badge 
                variant={isPositiveTrend ? "default" : isNegativeTrend ? "destructive" : "secondary"}
                className="text-[10px] sm:text-xs"
              >
                {isPositiveTrend ? "↑" : isNegativeTrend ? "↓" : "→"} {Math.abs(trend.value)}%
              </Badge>
              <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{trend.label}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
